import { Check, Close, InfoOutlined, PlaceOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import { getScoreColor, toPercent } from 'helpers/eligibility';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import type { AssessmentResult, AssessmentScore } from 'types';

interface EligibilityResultsModalProps {
    assessment: AssessmentResult;
    onClose: () => void;
    onBack?: () => void;
}

const ScoreRing: React.FC<{ value: number | null; color: string; size?: number }> = ({
    value,
    color,
    size = 128,
}) => {
    const progress = value ?? 0;

    return (
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <CircularProgress
                variant="determinate"
                value={100}
                size={size}
                thickness={3.5}
                sx={{
                    color: 'rgba(148, 163, 184, 0.18)',
                    position: 'absolute',
                    inset: 0,
                }}
            />
            <CircularProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, progress))}
                size={size}
                thickness={3.5}
                sx={{
                    color,
                    position: 'absolute',
                    inset: 0,
                    '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                    },
                }}
            />
            <Stack
                sx={{
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: size > 100 ? 32 : 22,
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        lineHeight: 1,
                        color,
                    }}
                >
                    {value !== null ? `${value}%` : '—'}
                </Typography>
            </Stack>
        </Box>
    );
};

const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: 0,
            px: 1.5,
            py: 1.25,
            borderRadius: 2.5,
            bgcolor: 'rgba(248, 250, 252, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.14)',
            textAlign: 'center',
        }}
    >
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                display: 'block',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 0.25,
                fontSize: 10,
            }}
        >
            {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value}
        </Typography>
    </Box>
);

const CategoryRow: React.FC<{ item: AssessmentScore; index: number }> = ({ item, index }) => {
    const { translate } = useTranslate();
    const statusLabel = translate(item.passed ? 'pass' : 'fail');
    const chipLabel = item.max_score
        ? `${item.score}/${item.max_score} · ${statusLabel}`
        : statusLabel;

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                border: '1px solid rgba(148, 163, 184, 0.12)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.22)',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
                },
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: item.explanation?.text ? 0.75 : 0,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, lineHeight: 1.35, color: 'text.primary', pr: 1 }}
                >
                    {index + 1}. {item.label}
                </Typography>
                <Chip
                    size="small"
                    icon={
                        item.passed ? (
                            <Check sx={{ fontSize: '14px !important' }} />
                        ) : (
                            <Close sx={{ fontSize: '14px !important' }} />
                        )
                    }
                    label={chipLabel}
                    sx={{
                        flexShrink: 0,
                        height: 26,
                        fontWeight: 700,
                        fontSize: 11,
                        borderRadius: '999px',
                        color: item.passed ? 'success.dark' : 'error.dark',
                        bgcolor: item.passed
                            ? 'rgba(16, 185, 129, 0.12)'
                            : 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid',
                        borderColor: item.passed
                            ? 'rgba(16, 185, 129, 0.28)'
                            : 'rgba(239, 68, 68, 0.28)',
                        '& .MuiChip-icon': {
                            color: item.passed ? 'success.main' : 'error.main',
                            ml: 0.75,
                        },
                    }}
                />
            </Stack>

            {item.explanation?.text ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.5 }}
                >
                    {item.explanation.text}
                </Typography>
            ) : null}
        </Box>
    );
};

const EligibilityResultsModal: React.FC<EligibilityResultsModalProps> = ({
    assessment,
    onClose,
    onBack,
}) => {
    const { translate } = useTranslate();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const additionalAdvice =
        language?.toLowerCase() === 'fr'
            ? assessment.additional_advice_fr
            : assessment.additional_advice_en;
    const immigrationAdvice = [assessment.immigration_advice, additionalAdvice]
        .filter(Boolean)
        .join('\n\n');

    const passed = Boolean(assessment?.summary?.passed);
    const score = assessment?.summary?.score ?? 0;
    const maxScore = assessment?.summary?.max_score ?? 0;
    const requiredScore = assessment?.summary?.required_score ?? 0;
    const summaryPct = toPercent(score, maxScore);
    const scoreColor = getScoreColor(score, maxScore);
    const location = [assessment.country, assessment.region].filter(Boolean).join(' · ');
    const programType = assessment.program_type;

    return (
        <Dialog open fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{translate('eligibilityResult')}</DialogTitle>

            <DialogContent
                sx={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    px: { xs: 2, sm: 2.5 },
                    bgcolor: 'rgba(248, 250, 252, 0.55)',
                }}
            >
                <Stack spacing={2.5} sx={{ pt: 2 }}>
                    {/* Program identity */}
                    <Stack spacing={1}>
                        <Typography variant="h5" sx={{ fontSize: 20, fontWeight: 700 }}>
                            {assessment.program_name}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}
                        >
                            {programType ? (
                                <Chip
                                    size="small"
                                    label={programType}
                                    sx={{
                                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                                        color: 'primary.dark',
                                    }}
                                />
                            ) : null}
                            {location ? (
                                <Chip
                                    size="small"
                                    icon={<PlaceOutlined sx={{ fontSize: '14px !important' }} />}
                                    label={location}
                                    sx={{
                                        bgcolor: 'rgba(148, 163, 184, 0.12)',
                                        color: 'text.secondary',
                                        '& .MuiChip-icon': { color: 'text.secondary' },
                                    }}
                                />
                            ) : null}
                        </Stack>
                    </Stack>

                    {/* Score hero */}
                    <Box
                        sx={{
                            p: { xs: 2.5, sm: 3 },
                            borderRadius: 3,
                            bgcolor: 'background.paper',
                            border: '1px solid rgba(148, 163, 184, 0.12)',
                            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2.5}
                            sx={{ alignItems: 'center' }}
                        >
                            <ScoreRing value={summaryPct} color={scoreColor} />

                            <Stack
                                spacing={1.5}
                                sx={{
                                    flex: 1,
                                    width: '100%',
                                    alignItems: { xs: 'center', sm: 'flex-start' },
                                    textAlign: { xs: 'center', sm: 'left' },
                                }}
                            >
                                <Chip
                                    icon={
                                        passed ? (
                                            <Check sx={{ fontSize: '16px !important' }} />
                                        ) : (
                                            <Close sx={{ fontSize: '16px !important' }} />
                                        )
                                    }
                                    label={translate(passed ? 'likelyEligible' : 'notEligible')}
                                    sx={{
                                        height: 32,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        borderRadius: '10px',
                                        px: 0.5,
                                        color: passed ? 'success.dark' : 'error.dark',
                                        bgcolor: passed
                                            ? 'rgba(16, 185, 129, 0.12)'
                                            : 'rgba(239, 68, 68, 0.1)',
                                        border: 'none',
                                        '& .MuiChip-icon': {
                                            color: passed ? 'success.main' : 'error.main',
                                        },
                                    }}
                                />

                                {maxScore > 0 ? (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ width: '100%', maxWidth: { sm: 320 } }}
                                    >
                                        <StatCard
                                            label={translate('programScore')}
                                            value={`${score} / ${maxScore}`}
                                        />
                                        {requiredScore ? (
                                            <StatCard
                                                label={translate('requiredScore')}
                                                value={requiredScore}
                                            />
                                        ) : null}
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Stack>
                    </Box>

                    {/* Category breakdown */}
                    {assessment.scores && assessment.scores.length > 0 ? (
                        <Stack spacing={1.25}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: 'text.primary', px: 0.25 }}
                            >
                                {translate('result')}
                            </Typography>
                            <Stack spacing={1}>
                                {assessment.scores.map((item, idx) => (
                                    <CategoryRow
                                        key={`${item.label}-${idx}`}
                                        item={item}
                                        index={idx}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 2.5,
                                bgcolor: 'background.paper',
                                border: '1px solid rgba(148, 163, 184, 0.12)',
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {translate('noCategoryScores')}
                            </Typography>
                        </Box>
                    )}

                    {/* Advice */}
                    {immigrationAdvice ? (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                bgcolor: 'rgba(99, 102, 241, 0.06)',
                                border: '1px solid rgba(99, 102, 241, 0.12)',
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(99, 102, 241, 0.12)',
                                        color: 'primary.main',
                                    }}
                                >
                                    <InfoOutlined sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, color: 'primary.dark' }}
                                >
                                    {translate('immigrationAdvice')}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, pl: 0.25 }}
                            >
                                {immigrationAdvice}
                            </Typography>
                        </Box>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    flexShrink: 0,
                    px: 2.5,
                    py: 1.75,
                    gap: 1,
                    borderTop: '1px solid rgba(148, 163, 184, 0.12)',
                    bgcolor: 'background.paper',
                }}
            >
                {onBack ? (
                    <Button
                        onClick={onBack}
                        color="inherit"
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                    >
                        {translate('back')}
                    </Button>
                ) : null}
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: '0 8px 16px rgba(99, 102, 241, 0.25)' },
                    }}
                >
                    {translate('done')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EligibilityResultsModal;
